import { DateTime } from "luxon";
import { useMemo } from "react";
import { MultiColumnCalendarGrid } from "@/domains/appointment/appointments/components/calendar/CalendarGrid";
import {
  OperatingHoursOverlay,
  useOperatingHoursForDate,
} from "@/domains/appointment/appointments/components/calendar/operating-hours-overlay";
import {
  createTimeSlotConfig,
  getCalendarThemeConfig,
  getTimeColumnWidth,
} from "@/domains/appointment/appointments/config/calendar-config";
import {
  calculateAppointmentPosition,
  calculateExtendedTimeRange,
  computeAppointmentLayoutMap,
  isToday,
  type PositionConfig,
} from "@/domains/appointment/appointments/utils/calendar-utils";
import { CalendarAppointmentCard } from "@/domains/appointment/calendar/components/calendar-appointment-card";
import { useOperatingHours } from "@/domains/settings/hooks/use-operating-hours";
import type { AppointmentBookingStatus } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useCalendarAppointments } from "../../hooks/use-calendar-appointments";
import { useCalendarFilters } from "../../hooks/use-calendar-filters";
import { useCalendarWorkers } from "../../hooks/use-calendar-workers";
import { useCalendarStateStore } from "../../store/use-calendar-state-store";
import type { AppointmentListFragment, WorkerWithUserFragment } from "../../types";
import { CurrentTimeLine } from "../ui/current-time-line";

interface WorkerViewProps {
  currentDate: DateTime;
  onAppointmentClick?: (appointmentId: string) => void;
  isSuggestionContext?: boolean;
}

export function WorkerView({
  currentDate,
  onAppointmentClick,
  isSuggestionContext = false,
}: WorkerViewProps) {
  // Ensure we have a valid date
  const luxonDate = currentDate?.isValid ? currentDate : DateTime.now();

  const { currentSalon } = useSalonStore();
  const { statusFilters, workerFilters } = useCalendarFilters();
  const { timeScale } = useCalendarStateStore();

  // Fetch operating hours
  const { operatingHours } = useOperatingHours(currentSalon?.id);

  // Get operating hours for this specific date
  const dayOperatingHours = useOperatingHoursForDate(operatingHours, luxonDate);

  // Fetch appointments for the current day
  const { appointments } = useCalendarAppointments({
    startDate: luxonDate.startOf("day"),
    endDate: luxonDate.endOf("day"),
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  // Calculate extended time range based on operating hours AND appointments
  const { startHour, endHour } = useMemo(() => {
    return calculateExtendedTimeRange(
      dayOperatingHours.map((h) => ({ openTime: h.startTime, closeTime: h.endTime })),
      appointments
    );
  }, [dayOperatingHours, appointments]);

  // Fetch workers directly using GraphQL hook
  const { workers } = useCalendarWorkers();

  // Configuration for time slots and positioning using dynamic scale
  const scaleConfig = getCalendarThemeConfig(timeScale);
  const safeStartHour = !Number.isNaN(startHour) ? startHour : 0;
  const safeEndHour = !Number.isNaN(endHour) ? endHour : 24;
  const timeConfig = createTimeSlotConfig(safeStartHour, safeEndHour, {
    hourHeight: scaleConfig.layout.hourHeight,
  });

  const positionConfig: PositionConfig = useMemo(
    () => ({
      hourHeight: scaleConfig.layout.hourHeight,
      minHeight: scaleConfig.layout.minAppointmentHeight,
    }),
    [scaleConfig.layout.hourHeight, scaleConfig.layout.minAppointmentHeight]
  );

  // Filter workers based on workerFilters and ensure they're active
  const filteredWorkers = useMemo(() => {
    return workers.filter((worker) => {
      const workerMatch = workerFilters.length === 0 || workerFilters.includes(worker.id);
      return workerMatch && worker.isActive;
    });
  }, [workers, workerFilters]);

  // Add "Unassigned" column for appointments without workers
  const workersWithUnassigned = useMemo(() => {
    return [
      ...filteredWorkers,
      {
        id: "unassigned",
        user: { firstName: "Unassigned", lastName: "" },
        isActive: true,
        color: "#ef4444",
      } as WorkerWithUserFragment,
    ];
  }, [filteredWorkers]);

  // Group appointments by worker and calculate positions
  const appointmentsByWorker = useMemo(() => {
    // Use all appointments since they are already filtered by the hook for the current day
    const dayAppointments = appointments;

    // Group appointments by worker with positioning
    const result: Record<
      string,
      Array<{
        appointment: AppointmentListFragment;
        style: React.CSSProperties;
        height: number;
      }>
    > = {};

    workersWithUnassigned.forEach((worker) => {
      const workerAppointments: Array<{
        appointment: AppointmentListFragment;
        style: React.CSSProperties;
        height: number;
      }> = [];

      dayAppointments.forEach((appointment) => {
        if (worker.id === "unassigned") {
          // For unassigned, show appointments with no assigned services or services with no worker
          const hasAssignedServices = appointment.appointmentServices?.some(
            (service) => service.worker?.id
          );
          if (!hasAssignedServices) {
            const unassignedAppointments = dayAppointments.filter(
              (apt) => !apt.appointmentServices?.some((s) => s.worker?.id)
            );
            const layoutMap = computeAppointmentLayoutMap(
              unassignedAppointments,
              positionConfig,
              safeStartHour
            );
            const position = calculateAppointmentPosition(
              appointment,
              positionConfig,
              safeStartHour
            );
            const layout = layoutMap.get(appointment.id) ?? {
              width: "calc(100% - 6px)",
              left: "3px",
              hasOverlap: false,
              overlapCount: 1,
              zIndex: 1,
            };

            workerAppointments.push({
              appointment,
              style: {
                position: "absolute" as const,
                top: `${position.top}px`,
                height: `${Math.max(position.height, positionConfig.minHeight || 24)}px`,
                left: layout.left,
                width: layout.width,
                zIndex: layout.zIndex,
              },
              height: Number.parseInt(
                `${Math.max(position.height, positionConfig.minHeight || 24)}`,
                10
              ),
            });
          }
        } else {
          // For assigned workers, show appointments that have services assigned to this worker
          const hasServicesForWorker = appointment.appointmentServices?.some(
            (service) => service.worker?.id === worker.id
          );
          if (hasServicesForWorker) {
            const workerAppointmentsList = dayAppointments.filter((apt) =>
              apt.appointmentServices?.some((s) => s.worker?.id === worker.id)
            );
            const layoutMap = computeAppointmentLayoutMap(
              workerAppointmentsList,
              positionConfig,
              safeStartHour
            );
            const position = calculateAppointmentPosition(
              appointment,
              positionConfig,
              safeStartHour
            );
            const layout = layoutMap.get(appointment.id) ?? {
              width: "calc(100% - 6px)",
              left: "3px",
              hasOverlap: false,
              overlapCount: 1,
              zIndex: 1,
            };

            workerAppointments.push({
              appointment,
              style: {
                position: "absolute" as const,
                top: `${position.top}px`,
                height: `${Math.max(position.height, positionConfig.minHeight || 24)}px`,
                left: layout.left,
                width: layout.width,
                zIndex: layout.zIndex,
              },
              height: Number.parseInt(
                `${Math.max(position.height, positionConfig.minHeight || 24)}`,
                10
              ),
            });
          }
        }
      });

      result[worker.id] = workerAppointments;
    });

    return result;
  }, [appointments, workersWithUnassigned, positionConfig, safeStartHour]);

  const handleAppointmentClick = (appointment: AppointmentListFragment) => {
    onAppointmentClick?.(appointment.id);
  };

  // Create headers component
  const _Headers = () => {
    // Calculate header layout to match the grid
    const _timeColumnWidth = getTimeColumnWidth();

    return (
      <div className="border border-border rounded-t-lg bg-card shadow-sm overflow-hidden bg-clip-padding flex-shrink-0">
        <div className={`flex ${!isSuggestionContext ? "min-w-max" : "w-full"}`}>
          {/* Time Column Header */}
          <div
            className={`${getTimeColumnWidth()} flex-shrink-0 h-12 border-r border-border bg-muted flex items-center justify-center`}
          >
            <span className="text-xs font-medium text-muted-foreground">Time</span>
          </div>

          {/* Worker Headers */}
          <div className="flex w-full">
            {workersWithUnassigned.map((worker) => {
              const workerColor = worker.color || "#6b7280";
              return (
                <div
                  key={worker.id}
                  className={`border-r border-border last:border-r-0 ${
                    isSuggestionContext ? "flex-1" : "flex-1 min-w-[320px] sm:flex-1 sm:min-w-0"
                  } ${worker.id === "unassigned" ? "bg-muted/50" : ""} h-12 flex items-center justify-center ${
                    worker.id === "unassigned"
                      ? "bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30"
                      : "bg-muted"
                  }`}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full border border-white flex-shrink-0"
                      style={{ backgroundColor: workerColor }}
                    />
                    <span
                      className={`text-[10px] sm:text-xs font-medium truncate ${
                        worker.id === "unassigned"
                          ? "text-red-700 dark:text-red-300"
                          : "text-foreground"
                      }`}
                    >
                      {worker.id === "unassigned" ? (
                        "Unassigned"
                      ) : (
                        <>
                          <span className="sm:hidden">
                            {worker.displayName || worker.user?.firstName || "Unknown Worker"}
                          </span>
                          <span className="hidden sm:inline">
                            {worker.displayName ||
                              (worker.user
                                ? `${worker.user.firstName} ${worker.user.lastName}`
                                : "Unknown Worker")}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Function to get worker availability blocks for the current day
  // Worker availability is not included in the WorkerWithUserFragment, so this returns empty.
  const getWorkerAvailabilityBlocks = (
    _worker: WorkerWithUserFragment
  ): Array<{ key: string; top: number; height: number; timeRange: string }> => {
    return [];
  };

  const renderWorkerColumn = (worker: WorkerWithUserFragment, workerIndex: number) => {
    const workerAppointments = appointmentsByWorker[worker.id] || [];
    const showCurrentTimeLine = isToday(luxonDate);
    const workerColor = worker.color || "#6b7280";

    return (
      <div
        key={worker.id}
        className={`border-r border-border last:border-r-0 ${
          isSuggestionContext
            ? "flex-1"
            : "flex-shrink-0 sm:flex-1 min-w-[320px] sm:min-w-0 sm:w-auto"
        } ${worker.id === "unassigned" ? "bg-muted/50" : ""}`}
      >
        {/* Time Slots Column */}
        <div
          className="relative"
          style={{
            minHeight: `${(safeEndHour - safeStartHour) * scaleConfig.layout.hourHeight}px`,
          }}
        >
          {/* Hour Dividers */}
          {Array.from({ length: safeEndHour - safeStartHour }).map((_, hour) => (
            <div
              key={`hour-${hour}`}
              className="border-b border-border dark:border-border last:border-b-0"
              style={{ height: `${scaleConfig.layout.hourHeight}px` }}
            />
          ))}
          {/* 15-minute interval lines */}
          {Array.from({ length: (safeEndHour - safeStartHour) * 4 }).map((_, interval) => {
            // Skip lines that coincide with hour lines (every 4th interval)
            if (interval % 4 === 0) return null;
            const topPx = interval * (scaleConfig.layout.hourHeight / 4);

            return (
              <div
                key={`interval-top-${topPx}`}
                className="absolute left-0 right-0 border-b border-border/30 dark:border-border/20 pointer-events-none"
                style={{
                  top: `${topPx}px`,
                  zIndex: 1,
                }}
              />
            );
          })}

          {/* Operating Hours Overlay */}
          <OperatingHoursOverlay
            operatingHours={dayOperatingHours}
            startTime={luxonDate.startOf("day").set({ hour: safeStartHour })}
            endTime={luxonDate.startOf("day").set({ hour: safeEndHour })}
            hourHeight={scaleConfig.layout.hourHeight}
            showLabels={false} // Keep minimal for worker view
            closedOpacity={0.3}
            className="z-0"
          />

          {/* Worker Availability Blocks */}
          {getWorkerAvailabilityBlocks(worker).map((block) => (
            <div
              key={block.key}
              className="absolute left-0 right-0 pointer-events-none opacity-30 z-0 rounded-sm"
              style={{
                top: `${block.top}px`,
                height: `${block.height}px`,
                backgroundColor: workerColor,
                borderLeft: `4px solid ${workerColor}`,
                borderRight: `1px solid ${workerColor}20`,
              }}
              title={`${worker.displayName || (worker.user ? `${worker.user.firstName} ${worker.user.lastName}` : "Unknown Worker")} - Available: ${block.timeRange}`}
            >
              <div
                className="absolute top-1 left-2 text-xs font-medium opacity-60"
                style={{ color: workerColor }}
              >
                Working Hours
              </div>
            </div>
          ))}
          {/* Unassigned column hint */}
          {worker.id === "unassigned" && workerAppointments.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="text-center p-4">
                <div className="text-xs text-muted-foreground mb-1">Drop appointments here</div>
                <div className="text-xs text-muted-foreground">to unassign worker</div>
              </div>
            </div>
          )}
          {/* Current Time Line - only show if viewing today */}
          {showCurrentTimeLine && (
            <CurrentTimeLine
              startHour={safeStartHour}
              endHour={safeEndHour}
              hourHeight={scaleConfig.layout.hourHeight}
              showTimeLabel={workerIndex === 0}
            />
          )}
          {/* Appointments */}
          {workerAppointments.map((item, _index) => {
            const key = item.appointment.id;
            const shadowClass = "shadow-sm";

            // Use CalendarAppointmentCard for all appointments to maintain consistent design
            return (
              <CalendarAppointmentCard
                key={key}
                appointment={item.appointment}
                height={item.height}
                pixelsPerHour={scaleConfig.layout.hourHeight}
                style={item.style}
                className={`${shadowClass} transition-shadow hover:shadow-lg`}
                isSuggestionContext={isSuggestionContext}
                onClick={handleAppointmentClick}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Combined Headers and Content with synchronized horizontal scroll */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-max sm:min-w-full">
          {/* Worker Headers - Sticky with responsive layout */}
          <div className="sticky top-0 z-50 border border-border rounded-t-lg bg-card shadow-sm overflow-hidden bg-clip-padding">
            <div className="flex min-w-max sm:w-full">
              <div
                className={`${getTimeColumnWidth()} flex-shrink-0 h-12 border-r border-border bg-muted flex items-center justify-center`}
              >
                <span className="text-xs font-medium text-muted-foreground">Time</span>
              </div>
              <div className="flex min-w-max sm:w-full">
                {workersWithUnassigned.map((worker) => {
                  return (
                    <div
                      key={worker.id}
                      className={`border-r border-border last:border-r-0 flex-shrink-0 sm:flex-1 min-w-[320px] sm:min-w-0 sm:w-auto ${worker.id === "unassigned" ? "bg-muted/50" : ""} h-12 flex items-center justify-center ${
                        worker.id === "unassigned"
                          ? "bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30"
                          : "bg-muted"
                      }`}
                    >
                      <div className="flex items-center justify-center min-w-0">
                        <span
                          className={`text-[10px] sm:text-xs font-medium truncate ${
                            worker.id === "unassigned"
                              ? "text-red-700 dark:text-red-300"
                              : "text-foreground"
                          }`}
                        >
                          {worker.id === "unassigned" ? (
                            "Unassigned"
                          ) : (
                            <>
                              <span className="sm:hidden">
                                {worker.displayName || worker.user?.firstName || "Unknown Worker"}
                              </span>
                              <span className="hidden sm:inline">
                                {worker.displayName ||
                                  (worker.user
                                    ? `${worker.user.firstName} ${worker.user.lastName}`
                                    : "Unknown Worker")}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          <MultiColumnCalendarGrid
            timeConfig={timeConfig}
            numberOfColumns={workersWithUnassigned.length}
            containerClassName="border-0 border-l border-r border-b border-border rounded-b-lg"
            className=""
            timeColumnWidth={getTimeColumnWidth()}
            columnWidth="auto"
            minHeight="auto"
            enableHorizontalScroll={true}
            responsiveColumns={true}
          >
            {workersWithUnassigned.map(renderWorkerColumn)}
          </MultiColumnCalendarGrid>
        </div>
      </div>
    </div>
  );
}
